import React, { useMemo, useState, useEffect } from "react";
import dayjs from "dayjs";
import moment from "moment";
import { Button } from "primereact/button";
import {
  GetViewAutoBudgetExpenseControl,
  GetYearBudget,
} from "../../Services/BudgetReportService";
import { Table, Row, Col, Select } from "antd";
import "./BudgetReportCR.css";
import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import type { ColumnsType } from "antd/es/table";

interface DataType {
  key: string;
  AccountCode: string;
  AccountName: string;
  SetBudget: number;
  RequestBudget: number;
  TransferredBudget: number;
  RequestBudgetApprove: number;
  AvailableBudget: number;
  ActualExpense: number;
  RemainingApprovedBudget: number;
  NetRemainingBudget: number;
}

const numberFormat = (value: number) =>
  Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
  });

function ExpenseBudgetControlFiscalYear() {
  const [loading, setLoading] = useState(false);
  const [pageSize] = useState<number>(10);
  const [selectedYear, setSelectedYear] = useState<string>(
    (new Date().getFullYear() + 543).toString(),
  );
  const [selectedDivision, setSelectedDivision] = useState<string[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<string[]>([]);

  // rawRow เก็บ data rows ทั้งหมด จาก API
  const [rawRow, setRawRow] = useState<any[]>([]);

  // Dropdown options
  const [yearOption, setYearOption] = useState<any[]>([]);
  const [divisionOption, setDivisionOption] = useState<any[]>([]);
  const [departmentOption, setDepartmentOption] = useState<any[]>([]);
  
  // เก็บ data ที่ Filter
  const [filterExpense, setFilterExpense] = useState<any[] | null>(null);
  const [filterFixedAsset, setFilterFixedAsset] = useState<any[] | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  // fetchData GetViewAutoBudgetExpenseControl
  const fetchData = async () => {
    setLoading(true);

    const resYear = await GetYearBudget({
      ViewName: "vw_BG03_BudgetControlReport",
    });

    if (resYear.rows && resYear.rows.length > 0) {
      const dataJson = {
        BudgetYear: selectedYear.toString(),
      };

      const res = await GetViewAutoBudgetExpenseControl(dataJson);

      if (res.rows && res.rows.length > 0) {
        // data response
        setRawRow(res.rows);

        // year Option
        const years = Array.from(
          new Set(
            resYear.rows
              .map((r: any) => r.BudgetYear)
              .filter((y: any) => y !== null),
          ),
        ).sort();
        
        setYearOption(
          years.map((y: any) => ({
            label: y,
            value: y,
          })),
        );
        const divisions = Array.from(
          new Set(
            res.rows.map((r: any) => r.Division).filter((d: any) => d != null),
          ),
        );
        setDivisionOption(divisions.map((d: any) => ({ label: d, value: d })));

      } else {
        setRawRow([]);
        setFilterExpense(null);
        setFilterFixedAsset(null);
        setDivisionOption([]);
        setDepartmentOption([]);
      }
    } else {
      setRawRow([]);
      setYearOption([]);
      setFilterExpense(null);
      setFilterFixedAsset(null);
      setDivisionOption([]);
      setDepartmentOption([]);
    }
    setLoading(false);
  };

  const expenseControlData: DataType[] = useMemo(() => {
    if (!rawRow.length) return [];

    const grouped: Record<string, DataType> = {};

    rawRow
      .filter((item) => item.TypeGroup === "expenses")
      .filter((item) => selectedDivision.length === 0 || selectedDivision.includes(item.Division))
      .filter((item) => selectedDepartment.length === 0 || selectedDepartment.includes(item.Department))
      .forEach((item: any) => {
        const key = `${item.AccountCode}_${item.AccountName}`;

        if (!grouped[key]) {
          grouped[key] = {
            key,
            AccountCode: item.AccountCode,
            AccountName: item.AccountName,
            SetBudget: 0,
            RequestBudget: 0,
            TransferredBudget: 0,
            RequestBudgetApprove: 0,
            AvailableBudget: 0,
            ActualExpense: 0,
            RemainingApprovedBudget: 0,
            NetRemainingBudget: 0,
          };
        }

        grouped[key].SetBudget += Number(item.SetBudget || 0);
        grouped[key].RequestBudget += Number(item.RequestBudget || 0);
        grouped[key].TransferredBudget += Number(item.TransferredBudget || 0);
        grouped[key].RequestBudgetApprove += Number(
          item.RequestBudgetApprove || 0,
        );
        grouped[key].AvailableBudget += Number(item.AvailableBudget || 0);
        grouped[key].ActualExpense += Number(item.ActualExpense || 0);
        grouped[key].RemainingApprovedBudget += Number(
          item.RemainingApprovedBudget || 0,
        );
        grouped[key].NetRemainingBudget += Number(item.NetRemainingBudget || 0);
      });

    return Object.values(grouped);
  }, [rawRow, selectedDivision, selectedDepartment]);

  const fixedAssetData: DataType[] = useMemo(() => {
    if (!rawRow.length) return [];

    const grouped: Record<string, DataType> = {};

    rawRow
      .filter((item) => item.TypeGroup === "FixedAssets")
      .filter((item) => selectedDivision.length === 0 || selectedDivision.includes(item.Division))
      .filter((item) => selectedDepartment.length === 0 || selectedDepartment.includes(item.Department))
      .forEach((item: any) => {
        const key = `${item.AccountCode}_${item.AccountName}`;

        if (!grouped[key]) {
          grouped[key] = {
            key,
            AccountCode: item.AccountCode,
            AccountName: item.AccountName,
            SetBudget: 0,
            RequestBudget: 0,
            TransferredBudget: 0,
            RequestBudgetApprove: 0,
            AvailableBudget: 0,
            ActualExpense: 0,
            RemainingApprovedBudget: 0,
            NetRemainingBudget: 0,
          };
        }

        grouped[key].SetBudget += Number(item.SetBudget || 0);
        grouped[key].RequestBudget += Number(item.RequestBudget || 0);
        grouped[key].TransferredBudget += Number(item.TransferredBudget || 0);
        grouped[key].RequestBudgetApprove += Number(
          item.RequestBudgetApprove || 0,
        );
        grouped[key].AvailableBudget += Number(item.AvailableBudget || 0);
        grouped[key].ActualExpense += Number(item.ActualExpense || 0);
        grouped[key].RemainingApprovedBudget += Number(
          item.RemainingApprovedBudget || 0,
        );
        grouped[key].NetRemainingBudget += Number(item.NetRemainingBudget || 0);
      });

    return Object.values(grouped);
  }, [rawRow, selectedDivision, selectedDepartment]);

  const buildColumns = (data: DataType[]): ColumnsType<DataType> => [
    {
      title: "รหัสบัญชี",
      dataIndex: "AccountCode",
      width: 120,
      fixed: "left",
      filterSearch: true,
      filters: Array.from(new Set(data.map((item) => item.AccountCode))).map(
        (code) => ({
          text: code,
          value: code,
        }),
      ),
      onFilter: (value, record) =>
        record.AccountCode.toLowerCase().includes(String(value).toLowerCase()),
    },
    {
      title: "ชื่อหมวดบัญชี",
      dataIndex: "AccountName",
      width: 250,
      fixed: "left",
      filterSearch: true,
      filters: Array.from(new Set(data.map((item) => item.AccountName))).map(
        (name) => ({
          text: name,
          value: name,
        }),
      ),
      onFilter: (value, record) =>
        record.AccountName.toLowerCase().includes(String(value).toLowerCase()),
    },
    {
      title: "งบประมาณตั้งไว้",
      dataIndex: "SetBudget",
      align: "right",
      render: numberFormat,
    },
    {
      title: "งบประมาณที่อนุมัติเพิ่มเติม",
      dataIndex: "RequestBudget",
      align: "right",
      render: numberFormat,
    },
    {
      title: "งบประมาณที่ถูกโอนย้าย",
      dataIndex: "TransferredBudget",
      align: "right",
      render: numberFormat,
    },
    {
      title: "งบประมาณที่อนุมัติจริง",
      dataIndex: "RequestBudgetApprove",
      align: "right",
      render: numberFormat,
    },
    {
      title: "งบประมาณคงเหลือสามารถใช้ได้",
      dataIndex: "AvailableBudget",
      align: "right",
      render: (value: number) => (
        <span style={{ fontWeight: 600 }}>{numberFormat(value)}</span>
      ),
    },
    {
      title: "ค่าใช้จ่ายเกิดจริง",
      dataIndex: "ActualExpense",
      align: "right",
      render: numberFormat,
    },
    {
      title: "งบประมาณที่อนุมัติใช้แล้วคงเหลือ",
      dataIndex: "RemainingApprovedBudget",
      align: "right",
      render: (value: number) => (
        <span style={{ fontWeight: 600 }}>{numberFormat(value)}</span>
      ),
    },
    {
      title: "งบประมาณคงเหลือสุทธิ",
      dataIndex: "NetRemainingBudget",
      align: "right",
      render: (value: number) => (
        <span style={{ fontWeight: 600 }}>{numberFormat(value)}</span>
      ),
    },
  ];

  // set Columns
  const expenseColumns = useMemo(
    () => buildColumns(expenseControlData),
    [expenseControlData],
  );

  const fixedAssetColumns = useMemo(
    () => buildColumns(fixedAssetData),
    [fixedAssetData],
  );

  // onChange Year
  const handleYearChange = async (year: any) => {
    setSelectedYear(year);
    setSelectedDivision([]);
    setSelectedDepartment([]);

    if (!year) return;

    try {
      setLoading(true);

      const dataJson = {
        BudgetYear: year.toString(),
      };

      const data = await GetViewAutoBudgetExpenseControl(dataJson);

      if (data?.rows?.length > 0) {
        setRawRow(data.rows);
        
        const divisions = Array.from(
          new Set(
            data.rows.map((r: any) => r.Division).filter((d: any) => d != null),
          ),
        );

        setDivisionOption(
          divisions.map((d: any) => ({
            label: d,
            value: d,
          })),
        );

        const departments = Array.from(
          new Set(
            data.rows
              .map((r: any) => r.Department)
              .filter((d: any) => d != null),
          ),
        );

        setDepartmentOption(
          departments.map((d: any) => ({
            label: d,
            value: d,
          })),
        );

      } else {
        setRawRow([]);
        setDivisionOption([]);
        setDepartmentOption([]);
      }

      setFilterExpense(null);
      setFilterFixedAsset(null);
    } catch (error) {
      console.error("Error loading year data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!rawRow.length) return;

    setFilterExpense(null);
    setFilterFixedAsset(null);

    if (selectedDivision.length === 0) {
      const departments = Array.from(new Set(rawRow.map((r) => r.Department)));
      setDepartmentOption(departments.map((d) => ({ label: d, value: d })));
      return;
    }

    const filteredDepartments = Array.from(
      new Set(
        rawRow
          .filter((r) => selectedDivision.includes(r.Division))
          .map((r) => r.Department)
      )
    );

    setDepartmentOption(
      filteredDepartments.map((d) => ({ label: d, value: d }))
    );

    setSelectedDepartment((prev) =>
      prev.filter((dept) => filteredDepartments.includes(dept))
    );
  }, [selectedDivision, rawRow]);

  useEffect(() => {
    setFilterExpense(null);
    setFilterFixedAsset(null);
  }, [selectedDepartment]);

  const exportFullReport = () => {
    const wb = XLSX.utils.book_new();
    const wsData: any[][] = [];
    const headerRowIndexes: number[] = [];

    // ถ้ามี filter
    const exportExpense =
      filterExpense !== null ? [...filterExpense] : [...expenseControlData];

    const exportFixedAsset =
      filterFixedAsset !== null ? [...filterFixedAsset] : [...fixedAssetData];

    // header
    wsData.push([
      "รายงานการควบคุมงบประมาณค่าใช้จ่ายของ ส.อ.ท. และสายงาน ประจำปี " +
        selectedYear,
    ]);
    wsData.push([]);

    // build Section
    const buildSection = (
      title: string,
      columns: any[],
      data: any[],
      totalLabel: string,
    ) => {
      wsData.push([title]);
      wsData.push([]);

      const headerRow1: any[] = [];
      const headerRow2: any[] = [];
      const leafColumns: any[] = [];

      columns.forEach((col: any) => {
        if (!col.children) {
          headerRow1.push(col.title);
          headerRow2.push("");
          leafColumns.push(col);
        } else {
          const childCount = col.children.length;

          headerRow1.push(col.title);
          for (let i = 1; i < childCount; i++) {
            headerRow1.push("");
          }

          col.children.forEach((child: any) => {
            headerRow2.push(child.title);
            leafColumns.push(child);
          });
        }
      });

      const headerStartIndex = wsData.length;
      wsData.push(headerRow1);
      wsData.push(headerRow2);

      headerRowIndexes.push(headerStartIndex);
      headerRowIndexes.push(headerStartIndex + 1);

      // data
      data.forEach((row: any) => {
        wsData.push(leafColumns.map((col: any) => row[col.dataIndex] ?? 0));
      });

      // total
      const totalRow: any[] = [];

      leafColumns.forEach((col: any, index: number) => {
        if (index === 0) totalRow.push(totalLabel);
        else if (index === 1) totalRow.push("");
        else {
          const sum = data.reduce(
            (s: number, r: any) => s + (Number(r[col.dataIndex]) || 0),
            0,
          );
          totalRow.push(sum);
        }
      });

      wsData.push(totalRow);
      wsData.push([]);
      wsData.push([]);
    };

    // section
    buildSection("ตารางควบคุมงบประมาณ", expenseColumns, exportExpense, "รวม");
    buildSection("สินทรัพย์ถาวร", fixedAssetColumns, exportFixedAsset, "รวม");

    wsData.push([]);
    wsData.push([]);

    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // style
    const range = XLSX.utils.decode_range(ws["!ref"] as string);
    const sectionTitles = ["ตารางควบคุมงบประมาณ", "สินทรัพย์ถาวร"];

    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[cellAddress]) continue;

        const value = ws[cellAddress].v;

        const isSectionTitle = sectionTitles.includes(value);
        const isMainTitle = R === 0 || R === 1;
        const isHeaderRow = headerRowIndexes.includes(R);

        const isNumber =
          typeof value === "number" ||
          (!isNaN(Number(value)) && value !== "" && value !== null);

        ws[cellAddress].s = {
          font: {
            bold: isSectionTitle || isMainTitle,
          },
          alignment: {
            vertical: "center",
            horizontal: isHeaderRow
              ? "center"
              : C === 0
                ? "left"
                : isNumber
                  ? "right"
                  : "left",
          },
          border:
            isSectionTitle || isMainTitle
              ? undefined
              : {
                  top: { style: "thin" },
                  bottom: { style: "thin" },
                  left: { style: "thin" },
                  right: { style: "thin" },
                },
        };

        if (isNumber && !isHeaderRow && C !== 0) {
          ws[cellAddress].t = "n";
          ws[cellAddress].z = "#,##0.00";
        }
      }
    }

    // auto width
    const colWidths: any[] = [];

    wsData[5].forEach((_: any, colIndex: number) => {
      if (colIndex === 0) {
        colWidths.push({ wch: 16 });
        return;
      }

      if (colIndex === 1) {
        colWidths.push({ wch: 28 });
        return;
      }

      let maxLength = 10;

      for (let i = 5; i < wsData.length; i++) {
        const cell = wsData[i][colIndex];
        if (cell !== undefined && cell !== null) {
          maxLength = Math.max(maxLength, cell.toString().length);
        }
      }

      colWidths.push({ wch: maxLength + 4 });
    });

    ws["!cols"] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, "รายงานการควบคุม");

    const excelBuffer = XLSX.write(wb, {
      bookType: "xlsx",
      type: "array",
    });

    saveAs(
      new Blob([excelBuffer], {
        type: "application/octet-stream",
      }),
      `FTIExpenseBudgetControlFiscalYear_${selectedYear}.xlsx`,
    );
  };

  const exportSectionReport = (
    fileName: string,
    sheetName: string,
    title: string,
    columns: ColumnsType<DataType>,
    data: DataType[],
    totalLabel: string,
  ) => {
    const wb = XLSX.utils.book_new();
    const wsData: any[][] = [];
    const headerRowIndexes: number[] = [];
    const dataForExport = data ?? [];

    // title
    wsData.push([
      "รายงานการควบคุมงบประมาณค่าใช้จ่ายของ ส.อ.ท. และสายงาน ประจำปี " +
        selectedYear,
    ]);
    wsData.push([]);
    wsData.push([title]);
    wsData.push([]);

    // header
    const exportableColumns = columns.filter(
      (col): col is any => "dataIndex" in col,
    );

    const headers = exportableColumns.map((col) => col.title as string);
    const headerRowIndex = wsData.length;
    wsData.push(headers);

    // data
    dataForExport.forEach((row) => {
      wsData.push(
        exportableColumns.map(
          (col: any) => row[col.dataIndex as keyof DataType] ?? "",
        ),
      );
    });

    // total
    const totalRow: any[] = [];

    exportableColumns.forEach((col: any, index: number) => {
      if (index === 0) {
        totalRow.push(totalLabel);
      } else if (index === 1) {
        totalRow.push("");
      } else {
        const sum = dataForExport.reduce(
          (s: number, r: any) => s + (Number(r[col.dataIndex]) || 0),
          0,
        );
        totalRow.push(sum);
      }
    });

    wsData.push(totalRow);

    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // style
    const range = XLSX.utils.decode_range(ws["!ref"] as string);
    const sectionTitles = ["ตารางควบคุมงบประมาณ", "สินทรัพย์ถาวร"];

    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[cellAddress]) continue;

        const value = ws[cellAddress].v;

        const isSectionTitle = sectionTitles.includes(value);
        const isMainTitle = R === 0 || R === 1;
        const isHeaderRow = R === headerRowIndex;

        const isNumber =
          typeof value === "number" ||
          (!isNaN(Number(value)) && value !== "" && value !== null);

        ws[cellAddress].s = {
          font: {
            bold: isSectionTitle || isMainTitle,
          },
          alignment: {
            vertical: "center",
            horizontal: isHeaderRow
              ? "center"
              : C === 0
                ? "left"
                : isNumber
                  ? "right"
                  : "left",
          },
          border:
            isSectionTitle || isMainTitle
              ? undefined
              : {
                  top: { style: "thin" },
                  bottom: { style: "thin" },
                  left: { style: "thin" },
                  right: { style: "thin" },
                },
        };

        if (isNumber && !isHeaderRow && C !== 0) {
          ws[cellAddress].t = "n";
          ws[cellAddress].z = "#,##0.00";
        }
      }
    }

    // column width
    const colWidths = headers.map((_, i) => {
      if (i === 0) return { wch: 16 };
      if (i === 1) return { wch: 28 };
      return { wch: 20 };
    });

    ws["!cols"] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    const excelBuffer = XLSX.write(wb, {
      bookType: "xlsx",
      type: "array",
    });

    saveAs(
      new Blob([excelBuffer], {
        type: "application/octet-stream",
      }),
      `${fileName}.xlsx`,
    );
  };

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <Row
        justify="center"
        align="middle"
        style={{
          backgroundColor: "#282F6A",
          padding: "1rem",
          borderRadius: "8px",
        }}
      >
        <Col
          span={24}
          className="text-header-report"
          style={{ textAlign: "center" }}
        >
          รายงานการควบคุมงบประมาณค่าใช้จ่ายของ ส.อ.ท. และสายงาน ประจำปี{" "}
          {selectedYear}
        </Col>
      </Row>

      {/* Filters */}
      <Row
        gutter={[16, 16]}
        style={{
          marginTop: "2rem",
          padding: "1.5rem",
          backgroundColor: "#f0f2f5",
          borderRadius: "12px",
          border: "1px solid #d9d9d9",
        }}
      >

        {/* ปีงบประมาณ */}
        <Col xs={24} sm={24} md={8} lg={6} xl={2}>
          <div className="filter-label">ปีงบประมาณ (Year)</div>
          <Select
            options={yearOption}
            value={selectedYear}
            onChange={handleYearChange}
            style={{ width: "100%" }}
            size="large"
          />
        </Col>

        <Col xs={24} sm={24} md={8} lg={8} xl={6}>
          <div className="filter-label">หน่วยงาน (Division)</div>
          <Select
            mode="multiple"
            options={divisionOption}
            value={selectedDivision}
            onChange={(value) => setSelectedDivision(value)}
            allowClear
            size="large"
            style={{ width: "100%" }}
            placeholder="เลือกหน่วยงาน (Division)"
          />
        </Col>

        <Col xs={24} sm={24} md={8} lg={8} xl={6}>
          <div className="filter-label">สายงาน (Department)</div>
          <Select
            mode="multiple"
            options={departmentOption}
            value={selectedDepartment}
            onChange={(value) => setSelectedDepartment(value)}
            allowClear
            style={{ width: "100%" }}
            placeholder="เลือกสายงาน (Department)"
            size="large"
          />
        </Col>

        {/* Export Full Report*/}
        <Col
          xs={24}
          sm={12}
          md={12}
          lg={18}
          xl={10}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
          <Button
            label="Export to Excel"
            icon="pi pi-file-excel"
            loading={loading}
            onClick={exportFullReport}
            className="button-export-excel-all"
          />
        </Col>
      </Row>

      {/* Table */}
      <Row justify="space-between" align="middle" style={{ marginTop: "2rem" }}>
        <div className="text-body-report">ตารางควบคุมงบประมาณ</div>
        <Button
          label="Export to Excel"
          icon="pi pi-file-excel"
          loading={loading}
          onClick={() =>
            exportSectionReport(
              `FTIFixedAssetBudgetControl_ตารางควบคุมงบประมาณ_${selectedYear}`,
              "ตารางควบคุมงบประมาณ",
              "ตารางควบคุมงบประมาณ",
              expenseColumns,
              filterExpense ?? expenseControlData,
              "รวม",
            )
          }
          className="button-export-excel"
        />
      </Row>

      <Row style={{ marginTop: "1rem" }}>
        <Col span={24}>
          <Table
            key={`expense-${selectedDivision.join(',')}-${selectedDepartment.join(',')}`}
            className="budget-table"
            columns={expenseColumns}
            dataSource={expenseControlData}
            rowKey="key"
            loading={loading}
            bordered
            style={{ width: "100%" }}
            scroll={{ x: "max-content" }}
            pagination={{ pageSize: pageSize }}
            size="small"
            summary={() => {
              const dataSource =
                filterExpense !== null ? filterExpense : expenseControlData;

              const totals = dataSource.reduce(
                (acc, row) => {
                  acc.SetBudget += Number(row.SetBudget || 0);
                  acc.RequestBudget += Number(row.RequestBudget || 0);
                  acc.TransferredBudget += Number(row.TransferredBudget || 0);
                  acc.RequestBudgetApprove += Number(
                    row.RequestBudgetApprove || 0,
                  );
                  acc.AvailableBudget += Number(row.AvailableBudget || 0);
                  acc.ActualExpense += Number(row.ActualExpense || 0);
                  acc.RemainingApprovedBudget += Number(
                    row.RemainingApprovedBudget || 0,
                  );
                  acc.NetRemainingBudget += Number(row.NetRemainingBudget || 0);
                  return acc;
                },
                {
                  SetBudget: 0,
                  RequestBudget: 0,
                  TransferredBudget: 0,
                  RequestBudgetApprove: 0,
                  AvailableBudget: 0,
                  ActualExpense: 0,
                  RemainingApprovedBudget: 0,
                  NetRemainingBudget: 0,
                },
              );

              return (
                <Table.Summary.Row
                  className="footer-summary-row"
                  style={{ fontWeight: "bold" }}
                >
                  <Table.Summary.Cell index={0}>รวม</Table.Summary.Cell>

                  <Table.Summary.Cell index={1} />

                  <Table.Summary.Cell index={2} align="right">
                    {numberFormat(totals.SetBudget)}
                  </Table.Summary.Cell>

                  <Table.Summary.Cell index={3} align="right">
                    {numberFormat(totals.RequestBudget)}
                  </Table.Summary.Cell>

                  <Table.Summary.Cell index={4} align="right">
                    {numberFormat(totals.TransferredBudget)}
                  </Table.Summary.Cell>

                  <Table.Summary.Cell index={5} align="right">
                    {numberFormat(totals.RequestBudgetApprove)}
                  </Table.Summary.Cell>

                  <Table.Summary.Cell index={6} align="right">
                    {numberFormat(totals.AvailableBudget)}
                  </Table.Summary.Cell>

                  <Table.Summary.Cell index={7} align="right">
                    {numberFormat(totals.ActualExpense)}
                  </Table.Summary.Cell>

                  <Table.Summary.Cell index={8} align="right">
                    {numberFormat(totals.RemainingApprovedBudget)}
                  </Table.Summary.Cell>

                  <Table.Summary.Cell index={9} align="right">
                    {numberFormat(totals.NetRemainingBudget)}
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              );
            }}
            onChange={(pagination, filters, sorter, extra) => {
              setFilterExpense(extra.currentDataSource);
            }}
          />
        </Col>
      </Row>

      {/* ตารางสินทรัพย์ถาวร */}
      <div>
        <Row
          justify="space-between"
          align="middle"
          style={{ marginTop: "2rem" }}
        >
          <div className="text-body-report">สินทรัพย์ถาวร</div>
          <Button
            label="Export to Excel"
            icon="pi pi-file-excel"
            loading={loading}
            onClick={() =>
              exportSectionReport(
                `FTIFixedAssetBudgetControl_สินทรัพย์ถาวร_${selectedYear}`,
                "สินทรัพย์ถาวร",
                "สินทรัพย์ถาวร",
                fixedAssetColumns,
                filterFixedAsset ?? fixedAssetData,
                "รวม",
              )
            }
            className="button-export-excel"
          />
        </Row>
        <Row style={{ marginTop: "1rem" }}>
          <Col span={24}>
            <Table
              key={`fixed-${selectedDivision.join(',')}-${selectedDepartment.join(',')}`}
              className="budget-table"
              columns={fixedAssetColumns}
              dataSource={fixedAssetData}
              rowKey="key"
              loading={loading}
              bordered
              style={{ width: "100%" }}
              scroll={{ x: "max-content" }}
              pagination={{ pageSize: pageSize }}
              size="small"
              summary={() => {
                const dataSource =
                  filterFixedAsset !== null ? filterFixedAsset : fixedAssetData;

                const totals = dataSource.reduce(
                  (acc, row) => {
                    acc.SetBudget += Number(row.SetBudget || 0);
                    acc.RequestBudget += Number(row.RequestBudget || 0);
                    acc.TransferredBudget += Number(row.TransferredBudget || 0);
                    acc.RequestBudgetApprove += Number(
                      row.RequestBudgetApprove || 0,
                    );
                    acc.AvailableBudget += Number(row.AvailableBudget || 0);
                    acc.ActualExpense += Number(row.ActualExpense || 0);
                    acc.RemainingApprovedBudget += Number(
                      row.RemainingApprovedBudget || 0,
                    );
                    acc.NetRemainingBudget += Number(
                      row.NetRemainingBudget || 0,
                    );
                    return acc;
                  },
                  {
                    SetBudget: 0,
                    RequestBudget: 0,
                    TransferredBudget: 0,
                    RequestBudgetApprove: 0,
                    AvailableBudget: 0,
                    ActualExpense: 0,
                    RemainingApprovedBudget: 0,
                    NetRemainingBudget: 0,
                  },
                );

                return (
                  <Table.Summary.Row
                    className="footer-summary-row"
                    style={{ fontWeight: "bold" }}
                  >
                    <Table.Summary.Cell index={0}>รวม</Table.Summary.Cell>

                    <Table.Summary.Cell index={1} />

                    <Table.Summary.Cell index={2} align="right">
                      {numberFormat(totals.SetBudget)}
                    </Table.Summary.Cell>

                    <Table.Summary.Cell index={3} align="right">
                      {numberFormat(totals.RequestBudget)}
                    </Table.Summary.Cell>

                    <Table.Summary.Cell index={4} align="right">
                      {numberFormat(totals.TransferredBudget)}
                    </Table.Summary.Cell>

                    <Table.Summary.Cell index={5} align="right">
                      {numberFormat(totals.RequestBudgetApprove)}
                    </Table.Summary.Cell>

                    <Table.Summary.Cell index={6} align="right">
                      {numberFormat(totals.AvailableBudget)}
                    </Table.Summary.Cell>

                    <Table.Summary.Cell index={7} align="right">
                      {numberFormat(totals.ActualExpense)}
                    </Table.Summary.Cell>

                    <Table.Summary.Cell index={8} align="right">
                      {numberFormat(totals.RemainingApprovedBudget)}
                    </Table.Summary.Cell>

                    <Table.Summary.Cell index={9} align="right">
                      {numberFormat(totals.NetRemainingBudget)}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                );
              }}
              onChange={(pagination, filters, sorter, extra) => {
                setFilterFixedAsset(extra.currentDataSource);
              }}
            />
          </Col>
        </Row>
      </div>
    </div>
  );
}

export default ExpenseBudgetControlFiscalYear;
